import { BaseService, Body, ExtraMap, GET, Header, Path, POST, PUT, QueryMap, Signal } from 'ts-retrofit3';

import type { IExtra, IResponse } from '@/Module/Core/Model/ApiModel';
import type { IPaginationRequest } from '@/Module/Core/Model/DataModel';
import { HttpService } from '@/Module/Core/Service';
import type { ICourse } from '@/Module/Lesson/Model/DataModel';
import { SUBSCRIPTION_API } from '@/Module/Subscription/Constant';
import type {
  ICalcPrice,
  IReverseCoupon,
  IRubikaPaymentRequest,
  IRubikaPaymentResponse,
  IRubikaPaymentVerify,
  IShadPaymentRequest,
  IShadPaymentResponse,
  IShadPaymentVerify,
  IShadVerifyResponse,
  ISubscriptionRequest,
  ITransactionGet,
  IVerifyPaymentScript,
} from '@/Module/Subscription/Model/ApiModel';
import type {
  ISubscriptionPlan,
  ISubscriptionsV2User,
  ISubscriptionType,
  ITransactionPlan,
} from '@/Module/Subscription/Model/DataModel';

class SubscriptionService extends BaseService {
  @GET(SUBSCRIPTION_API.GET_TYPE)
  async getTypeList(@Signal _signal?: AbortSignal): Promise<IResponse<ISubscriptionType[]>> {
    return <IResponse>{};
  }

  @GET(SUBSCRIPTION_API.GET_PANEL)
  async getPenalList(
    @QueryMap _query: IPaginationRequest,
    @Signal _signal: AbortSignal,
  ): Promise<IResponse<ISubscriptionPlan[]>> {
    return <IResponse>{};
  }

  @GET(SUBSCRIPTION_API.GET_SUBSCRIPTIONS_V2_USER)
  async getSubscriptionsV2User(
    @Header('serverAuthorization') _authorization?: string,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ISubscriptionsV2User[]>> {
    return <IResponse>{};
  }

  @GET(SUBSCRIPTION_API.GET_TRANSACTION_LIST)
  async getTransactionUser(
    @QueryMap _query: ITransactionGet,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ITransactionPlan[]>> {
    return <IResponse>{};
  }

  @POST(SUBSCRIPTION_API.BUY_SUBSCRIPTION)
  async postSubscriptionRequest(
    @QueryMap _query: ISubscriptionRequest,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse<string>> {
    return <IResponse>{};
  }

  @POST(SUBSCRIPTION_API.CALC_PRICE_SUBSCRIPTION)
  async getCalcPriceSubscription(
    @QueryMap _query: ICalcPrice,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse<number>> {
    return <IResponse>{};
  }

  @GET(SUBSCRIPTION_API.VERIFY_PAYMENT)
  async postVerifyPaymentRequest(
    @Path('order_id') _orderId: number,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse<IVerifyPaymentScript>> {
    return <IResponse>{};
  }

  @GET(SUBSCRIPTION_API.COURSE_HAS_SUBSCRIPTION)
  async courseCheckHasSubscription(
    @Path('courseId') _courseId: ICourse['id'],
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
    @Header('serverAuthorization') _authorization?: string,
  ): Promise<IResponse<boolean>> {
    return <IResponse>{};
  }

  @GET(SUBSCRIPTION_API.COURSE_HAS_SUBSCRIPTION)
  async courseCheckHasSubscriptionV2(
    @Path('courseId') _courseId: ICourse['id'],
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
    @Header('serverAuthorization') _authorization?: string,
  ): Promise<IResponse<boolean>> {
    return <IResponse>{};
  }

  @POST(SUBSCRIPTION_API.RUBIKA_PAYMENT)
  async rubikaPaymentRequest(
    @Body _body: IRubikaPaymentRequest,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse<IRubikaPaymentResponse>> {
    return <IResponse>{};
  }

  @PUT(SUBSCRIPTION_API.RUBIKA_PAYMENT_VERIFY)
  async rubikaPaymentVerify(
    @Header('serverAuthorization') _authorization: string,
    @Body _body: Partial<Omit<IRubikaPaymentVerify, 'accessToken'>>,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse> {
    return <IResponse>{};
  }

  @POST(SUBSCRIPTION_API.REVERSE_COUPON)
  async reverseCoupon(
    @Body _body: IReverseCoupon,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse> {
    return <IResponse>{};
  }

  @POST(SUBSCRIPTION_API.SHAD)
  async shadPaymentRequest(@Body _body: IShadPaymentRequest): Promise<IResponse<IShadPaymentResponse>> {
    return <IResponse>{};
  }

  @PUT(SUBSCRIPTION_API.SHAD_PAYMENT_VERIFY)
  async shadPaymentVerify(
    @Header('SecurityToken') _authorizationShad: string,
    @Header('serverAuthorization') _authorization: string,
    @Body _body: Partial<Omit<IShadPaymentVerify, 'accessToken'>>,
    @Signal _signal?: AbortSignal,
    @ExtraMap _extra?: IExtra,
  ): Promise<IResponse<IShadVerifyResponse>> {
    return <IResponse>{};
  }
}

export default HttpService.build(SubscriptionService);
